import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { ZoomIn, RotateCcw } from 'lucide-react';

export const useGanttZoom = ({
  chartRef,
  scrollWrapperRef,
  minDate,
  maxDate,
  totalChartWidth,
  scaleFactor,
  setScaleFactor,
}) => {
  // Stały tryb lupy (włączany przyciskiem w pasku narzędzi)
  const [isZoomMode, setIsZoomMode] = useState(false);
  // Tymczasowy tryb lupy (aktywny podczas trzymania Ctrl + Spacja)
  const [isCtrlSpacePressed, setIsCtrlSpacePressed] = useState(false);
  const [zoomHistory, setZoomHistory] = useState([]);

  // Stany dla przesuwania widoku (Pan / Łapka) - sama Spacja lub ŚPM
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef(null);

  // Referencje śledzące wciśnięte klawisze w czasie rzeczywistym
  const spaceHeldRef = useRef(false);
  const ctrlHeldRef = useRef(false);

  // Referencje do rysowania ramki bezpośrednio w DOM (zero re-renderów podczas ruchu myszą)
  const zoomBoxRef = useRef(null);
  const boxDomRef = useRef(null);

  // Przechowuje docelowy scroll, który zostanie zaaplikowany synchronicznie przed odmalowaniem ekranu
  const pendingScrollRef = useRef(null);

  // Lupa jest aktywna, gdy włączono ją przyciskiem LUB gdy trzymane są Ctrl + Spacja
  const effectiveZoomMode = isZoomMode || isCtrlSpacePressed;

  // Synchroniczna aktualizacja pozycji scrolla w tej samej klatce co zmiana skali (brak skoku)
  useLayoutEffect(() => {
    if (!pendingScrollRef.current || !scrollWrapperRef.current) return;

    const { scrollLeft, scrollTop } = pendingScrollRef.current;
    scrollWrapperRef.current.scrollLeft = scrollLeft;
    scrollWrapperRef.current.scrollTop = scrollTop;
    pendingScrollRef.current = null;
  }, [scaleFactor, totalChartWidth, scrollWrapperRef]);

  const hideZoomBox = useCallback(() => {
    zoomBoxRef.current = null;
    if (boxDomRef.current) {
      boxDomRef.current.style.display = 'none';
    }
  }, []);

  const updateZoomBoxDom = useCallback(() => {
    const box = zoomBoxRef.current;
    const el = boxDomRef.current;
    if (!box || !el) return;

    const left = Math.min(box.startX, box.currentX);
    const top = Math.min(box.startY, box.currentY);
    const width = Math.abs(box.currentX - box.startX);
    const height = Math.abs(box.currentY - box.startY);

    el.style.display = 'block';
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
  }, []);

  const startPanning = useCallback(
    (clientX, clientY) => {
      const wrapper = scrollWrapperRef.current;
      if (!wrapper) return;

      hideZoomBox();
      setIsPanning(true);
      panStartRef.current = {
        startClientX: clientX,
        startClientY: clientY,
        startScrollLeft: wrapper.scrollLeft,
        startScrollTop: wrapper.scrollTop,
      };
    },
    [scrollWrapperRef, hideZoomBox]
  );

  // Synchronizacja trybu pracy na podstawie aktualnie wciśniętych klawiszy (Spacja vs Ctrl + Spacja)
  const syncKeyboardModes = useCallback(() => {
    const space = spaceHeldRef.current;
    const ctrl = ctrlHeldRef.current;

    if (space && ctrl) {
      // Ctrl + Spacja -> tymczasowy tryb zaznaczania obszaru powiększenia
      setIsCtrlSpacePressed(true);
      setIsSpacePressed(false);
      setIsPanning(false);
      panStartRef.current = null;
    } else if (space && !ctrl) {
      // Sama Spacja -> tryb przesuwania widoku (łapka)
      setIsCtrlSpacePressed(false);
      setIsSpacePressed(true);
      hideZoomBox();
    } else {
      // Spacja puszczona -> wyłączenie trybów tymczasowych
      setIsCtrlSpacePressed(false);
      setIsSpacePressed(false);
      setIsPanning(false);
      panStartRef.current = null;
      if (!isZoomMode) {
        hideZoomBox();
      }
    }
  }, [isZoomMode, hideZoomBox]);

  // 1. Globalna obsługa klawiatury (Ctrl+Spacja = lupa, Spacja = łapka, ESC = wyłącz lupę)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        e.target?.isContentEditable
      ) {
        return;
      }

      if (e.key === 'Escape') {
        if (isZoomMode) setIsZoomMode(false);
        hideZoomBox();
        return;
      }

      if (e.code === 'Space') {
        e.preventDefault(); // Blokuje przewijanie strony spacją
        spaceHeldRef.current = true;
        ctrlHeldRef.current = e.ctrlKey;
        syncKeyboardModes();
      } else if (e.key === 'Control') {
        ctrlHeldRef.current = true;
        if (spaceHeldRef.current) {
          syncKeyboardModes();
        }
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        spaceHeldRef.current = false;
        ctrlHeldRef.current = e.ctrlKey;
        syncKeyboardModes();
      } else if (e.key === 'Control') {
        ctrlHeldRef.current = false;
        if (spaceHeldRef.current) {
          syncKeyboardModes();
        }
      }
    };

    const handleWindowBlur = () => {
      spaceHeldRef.current = false;
      ctrlHeldRef.current = false;
      setIsCtrlSpacePressed(false);
      setIsSpacePressed(false);
      setIsPanning(false);
      hideZoomBox();
      panStartRef.current = null;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [isZoomMode, hideZoomBox, syncKeyboardModes]);

  // 2. Obsługa środkowego przycisku myszy bezpośrednio na wykresie
  useEffect(() => {
    const wrapper = scrollWrapperRef.current;
    if (!wrapper) return;

    const handleWrapperMouseDown = (e) => {
      if (e.button === 1) {
        e.preventDefault();
        e.stopPropagation();
        startPanning(e.clientX, e.clientY);
      }
    };

    const handleWrapperAuxClick = (e) => {
      if (e.button === 1) {
        e.preventDefault();
      }
    };

    wrapper.addEventListener('mousedown', handleWrapperMouseDown);
    wrapper.addEventListener('auxclick', handleWrapperAuxClick);

    return () => {
      wrapper.removeEventListener('mousedown', handleWrapperMouseDown);
      wrapper.removeEventListener('auxclick', handleWrapperAuxClick);
    };
  }, [scrollWrapperRef, startPanning]);

  // 3. Płynne przesuwanie na poziomie window
  useEffect(() => {
    if (!isPanning) return;

    const handleWindowMouseMove = (e) => {
      if (!panStartRef.current || !scrollWrapperRef.current) return;
      e.preventDefault();

      const dx = e.clientX - panStartRef.current.startClientX;
      const dy = e.clientY - panStartRef.current.startClientY;

      scrollWrapperRef.current.scrollLeft = panStartRef.current.startScrollLeft - dx;
      scrollWrapperRef.current.scrollTop = panStartRef.current.startScrollTop - dy;
    };

    const handleWindowMouseUp = () => {
      setIsPanning(false);
      panStartRef.current = null;
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [isPanning, scrollWrapperRef]);

  const toggleZoomMode = () => {
    setIsZoomMode((prev) => !prev);
    hideZoomBox();
  };

  // Cofanie o jeden krok wstecz (do poprzedniego obszaru)
  const handleRestoreZoom = useCallback(() => {
    if (zoomHistory.length === 0 || !scrollWrapperRef.current) return;

    const previousView = zoomHistory[zoomHistory.length - 1];
    setZoomHistory((prev) => prev.slice(0, -1));

    if (previousView.scale === scaleFactor) {
      scrollWrapperRef.current.scrollLeft = previousView.scrollLeft;
      scrollWrapperRef.current.scrollTop = previousView.scrollTop;
    } else {
      pendingScrollRef.current = {
        scrollLeft: previousView.scrollLeft,
        scrollTop: previousView.scrollTop,
      };
      setScaleFactor(previousView.scale);
    }
  }, [zoomHistory, scrollWrapperRef, scaleFactor, setScaleFactor]);

  const handleMouseDown = (e) => {
    if (!chartRef.current || !scrollWrapperRef.current) return;

    // 1. Środkowy przycisk myszy LUB (sama Spacja + lewy przycisk) -> przesuwanie widoku (Pan)
    if (e.button === 1 || (e.button === 0 && isSpacePressed)) {
      e.preventDefault();
      e.stopPropagation();
      startPanning(e.clientX, e.clientY);
      return;
    }

    if (!effectiveZoomMode) return;

    // 2. Prawy przycisk myszy w trybie lupy -> cofnij powiększenie
    if (e.button === 2) {
      e.preventDefault();
      e.stopPropagation();
      handleRestoreZoom();
      return;
    }

    // 3. Lewy przycisk myszy w trybie lupy (lub Ctrl+Spacja) -> rysowanie ramki powiększenia
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();

    const rect = chartRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    zoomBoxRef.current = { startX: x, startY: y, currentX: x, currentY: y };
    updateZoomBoxDom();
  };

  const handleMouseMove = (e) => {
    if (isPanning || !zoomBoxRef.current || !chartRef.current) return;

    const rect = chartRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, totalChartWidth));
    const y = Math.max(0, e.clientY - rect.top);

    zoomBoxRef.current.currentX = x;
    zoomBoxRef.current.currentY = y;
    updateZoomBoxDom();
  };

  const handleMouseUp = (e) => {
    if (isPanning) return;
    if (e && e.button !== 0) return;

    const box = zoomBoxRef.current;
    if (!box || !scrollWrapperRef.current || !minDate || !maxDate) return;

    const wrapper = scrollWrapperRef.current;

    setZoomHistory((prev) => [
      ...prev,
      {
        scale: scaleFactor,
        scrollLeft: wrapper.scrollLeft,
        scrollTop: wrapper.scrollTop,
      },
    ]);

    const minX = Math.min(box.startX, box.currentX);
    const maxX = Math.max(box.startX, box.currentX);
    const minY = Math.min(box.startY, box.currentY);
    const maxY = Math.max(box.startY, box.currentY);

    const boxWidthPx = maxX - minX;
    const centerY = minY + (maxY - minY) / 2;
    const containerWidth = wrapper.clientWidth;
    const containerHeight = wrapper.clientHeight;

    let targetScale = scaleFactor;
    let centerMinutesFromStart = 0;

    if (boxWidthPx < 15) {
      targetScale = Math.min(scaleFactor * 2, 8.0);
      centerMinutesFromStart = minX / scaleFactor;
    } else {
      const selectedMinutes = boxWidthPx / scaleFactor;
      centerMinutesFromStart = (minX + boxWidthPx / 2) / scaleFactor;

      if (selectedMinutes > 0) {
        targetScale = (containerWidth - 40) / selectedMinutes;
        targetScale = Math.min(Math.max(targetScale, 0.05), 8.0);
      }
    }

    const newCenterPx = centerMinutesFromStart * targetScale;
    const targetScrollLeft = Math.max(0, newCenterPx - containerWidth / 2);
    const targetScrollTop = Math.max(0, centerY - containerHeight / 2);

    hideZoomBox();

    if (targetScale === scaleFactor) {
      wrapper.scrollLeft = targetScrollLeft;
      wrapper.scrollTop = targetScrollTop;
    } else {
      pendingScrollRef.current = {
        scrollLeft: targetScrollLeft,
        scrollTop: targetScrollTop,
      };
      setScaleFactor(targetScale);
    }
  };

  const handleMouseLeave = () => {
    hideZoomBox();
  };

  return {
    isZoomMode: effectiveZoomMode,
    boxDomRef,
    zoomHistory,
    isSpacePressed,
    isPanning,
    toggleZoomMode,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleRestoreZoom,
    handleMouseLeave,
  };
};

export const GanttZoomButtons = ({ zoom, buttonClassName }) => {
  const historyCount = zoom.zoomHistory.length;

  return (
    <>
      <button
        className={buttonClassName}
        onClick={zoom.toggleZoomMode}
        style={{ backgroundColor: zoom.isZoomMode ? '#e63946' : '#63636396' }}
        title="Lupa (lub trzymaj Ctrl+Spacja): LPM zaznacza/przybliża, PPM cofa."
      >
        <ZoomIn size={20} />
      </button>

      {historyCount > 0 && (
        <button
          className={buttonClassName}
          onClick={zoom.handleRestoreZoom}
          style={{
            backgroundColor: '#457b9d',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
          title={`Cofnij do poprzedniego obszaru (pozostało kroków: ${historyCount})`}
        >
          <RotateCcw size={18} />
          <span style={{ fontSize: '11px', fontWeight: 700 }}>{historyCount}</span>
        </button>
      )}
    </>
  );
};

export const GanttZoomOverlay = ({ zoom }) => {
  const isOverlayActive = zoom.isZoomMode || zoom.isSpacePressed || zoom.isPanning;
  if (!isOverlayActive) return null;

  let activeCursor = 'zoom-in';
  if (zoom.isPanning) {
    activeCursor = 'grabbing';
  } else if (zoom.isSpacePressed) {
    activeCursor = 'grab';
  }

  return (
    <div
      onMouseDown={zoom.handleMouseDown}
      onMouseMove={zoom.handleMouseMove}
      onMouseUp={zoom.handleMouseUp}
      onMouseLeave={zoom.handleMouseLeave}
      onContextMenu={(e) => {
        if (zoom.isZoomMode) e.preventDefault();
      }}
      onAuxClick={(e) => {
        if (e.button === 1) e.preventDefault();
      }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 95,
        cursor: activeCursor,
        userSelect: 'none',
      }}
    >
      {zoom.isZoomMode && (
        <div
          ref={zoom.boxDomRef}
          style={{
            display: 'none',
            position: 'absolute',
            border: '2px dashed #e63946',
            backgroundColor: 'rgba(230, 57, 70, 0.15)',
            borderRadius: '4px',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};